import type { ItemStackNbt } from "../models/itemStack";

const EMPTY_NBT: ItemStackNbt = Object.freeze({});

// A live inventory holds the same stack strings across pushes, and every tile parses its own on each
// one. Handing back the identical object also keeps the skull owner stable, which is what stops
// MinecraftItem from re-resolving and re-rendering a head that did not change.
const parseCache = new Map<string, ItemStackNbt>();
const PARSE_CACHE_LIMIT = 2048;

// Subset SNBT reader: only tags the UI renders. Input format matches Remotex.Nbt/SnbtSerializer.cs.
export function parseSnbt(input: string | undefined): ItemStackNbt {
    if (!input || input.length === 0) return EMPTY_NBT;
    const cached = parseCache.get(input);
    if (cached) return cached;
    const parsed = readSnbt(input);
    if (parseCache.size >= PARSE_CACHE_LIMIT) parseCache.clear();
    parseCache.set(input, parsed);
    return parsed;
}

function readSnbt(input: string): ItemStackNbt {
    try {
        const reader = new Reader(input);
        reader.skipWs();
        if (reader.peek() !== "{") return EMPTY_NBT;
        const compound = reader.readCompound();
        return mapCompound(compound);
    } catch {
        return EMPTY_NBT;
    }
}

type SNode =
    | { kind: "compound"; value: Map<string, SNode> }
    | { kind: "list"; value: SNode[] }
    | { kind: "string"; value: string }
    | { kind: "number"; value: number };

class Reader {
    private i = 0;
    constructor(private readonly src: string) {}

    peek(): string {
        return this.src[this.i];
    }

    eof(): boolean {
        return this.i >= this.src.length;
    }

    skipWs(): void {
        while (!this.eof() && /\s/.test(this.src[this.i])) this.i++;
    }

    expect(ch: string): void {
        if (this.src[this.i] !== ch) throw new Error(`expected ${ch} at ${this.i}`);
        this.i++;
    }

    readCompound(): SNode {
        this.expect("{");
        const map = new Map<string, SNode>();
        this.skipWs();
        if (this.peek() === "}") {
            this.i++;
            return { kind: "compound", value: map };
        }
        while (true) {
            this.skipWs();
            const key = this.readKey();
            this.skipWs();
            this.expect(":");
            this.skipWs();
            const val = this.readValue();
            map.set(key, val);
            this.skipWs();
            if (this.peek() === ",") {
                this.i++;
                continue;
            }
            this.expect("}");
            break;
        }
        return { kind: "compound", value: map };
    }

    readList(): SNode {
        this.expect("[");
        const items: SNode[] = [];
        this.skipWs();
        if (/[IBL]/.test(this.peek()) && this.src[this.i + 1] === ";") {
            this.i += 2;
            this.skipWs();
        }
        if (this.peek() === "]") {
            this.i++;
            return { kind: "list", value: items };
        }
        while (true) {
            this.skipWs();
            items.push(this.readValue());
            this.skipWs();
            if (this.peek() === ",") {
                this.i++;
                continue;
            }
            this.expect("]");
            break;
        }
        return { kind: "list", value: items };
    }

    readKey(): string {
        if (this.peek() === '"' || this.peek() === "'") return this.readString();
        let out = "";
        while (!this.eof() && /[A-Za-z0-9_+\-.]/.test(this.src[this.i])) {
            out += this.src[this.i++];
        }
        if (out.length === 0) throw new Error("empty key");
        return out;
    }

    readString(): string {
        const quote = this.src[this.i++];
        let out = "";
        while (!this.eof() && this.src[this.i] !== quote) {
            if (this.src[this.i] === "\\") {
                this.i++;
                out += this.src[this.i++] ?? "";
            } else {
                out += this.src[this.i++];
            }
        }
        this.expect(quote);
        return out;
    }

    readValue(): SNode {
        const c = this.peek();
        if (c === "{") return this.readCompound();
        if (c === "[") return this.readList();
        if (c === '"' || c === "'") return { kind: "string", value: this.readString() };
        let raw = "";
        while (!this.eof() && /[A-Za-z0-9_+\-.]/.test(this.src[this.i])) {
            raw += this.src[this.i++];
        }
        if (raw.length === 0) throw new Error(`unexpected ${c} at ${this.i}`);
        const numMatch = /^(-?\d+(?:\.\d+)?)([bslfd])?$/i.exec(raw);
        if (numMatch) {
            const n = Number(numMatch[1]);
            if (!Number.isNaN(n)) return { kind: "number", value: n };
        }
        return { kind: "string", value: raw };
    }
}

function mapCompound(root: SNode): ItemStackNbt {
    if (root.kind !== "compound") return {};
    const out: ItemStackNbt = {};
    const m = root.value;
    out.tagCount = m.size;

    // An enchanted book carries its enchantments as StoredEnchantments, which the vanilla tooltip lists the same way.
    const ench = m.get("ench") ?? m.get("Enchantments") ?? m.get("StoredEnchantments");
    if (ench?.kind === "list") {
        const list: { id: number; lvl: number }[] = [];
        for (const entry of ench.value) {
            if (entry.kind !== "compound") continue;
            const id = entry.value.get("id");
            const lvl = entry.value.get("lvl");
            if (id?.kind === "number" && lvl?.kind === "number") {
                list.push({ id: id.value, lvl: lvl.value });
            }
        }
        if (list.length > 0) out.ench = list;
    }

    const display = m.get("display");
    if (display?.kind === "compound") {
        const name = display.value.get("Name");
        if (name?.kind === "string") out.displayName = name.value;
        const lore = display.value.get("Lore");
        if (lore?.kind === "list") {
            const lines: string[] = [];
            for (const line of lore.value) {
                if (line.kind === "string") lines.push(line.value);
            }
            if (lines.length > 0) out.lore = lines;
        }
    }

    const potion = m.get("Potion");
    if (potion?.kind === "string") out.potion = potion.value;

    const cpe = m.get("CustomPotionEffects");
    if (cpe?.kind === "list") {
        const list: { id: number; amplifier: number; duration: number }[] = [];
        for (const entry of cpe.value) {
            if (entry.kind !== "compound") continue;
            const id = entry.value.get("Id");
            const amp = entry.value.get("Amplifier");
            const dur = entry.value.get("Duration");
            if (id?.kind === "number" && amp?.kind === "number" && dur?.kind === "number") {
                list.push({ id: id.value, amplifier: amp.value, duration: dur.value });
            }
        }
        if (list.length > 0) out.customPotionEffects = list;
    }

    const unbreakable = m.get("Unbreakable");
    if (unbreakable?.kind === "number") out.unbreakable = unbreakable.value !== 0;

    const repairCost = m.get("RepairCost");
    if (repairCost?.kind === "number") out.repairCost = repairCost.value;

    const map3 = m.get("map3");
    if (map3?.kind === "string") out.map3 = map3.value;

    const skull = m.get("SkullOwner") ?? m.get("Owner");
    if (skull?.kind === "string") {
        out.skullOwner = { name: skull.value };
    } else if (skull?.kind === "compound") {
        const owner: { id?: string; name?: string; textureValue?: string } = {};
        const id = skull.value.get("Id");
        if (id?.kind === "string") owner.id = id.value;
        const name = skull.value.get("Name");
        if (name?.kind === "string") owner.name = name.value;
        const props = skull.value.get("Properties");
        if (props?.kind === "compound") {
            const textures = props.value.get("textures");
            const first = textures?.kind === "list" ? textures.value[0] : undefined;
            if (first?.kind === "compound") {
                const value = first.value.get("Value");
                if (value?.kind === "string") owner.textureValue = value.value;
            }
        }
        out.skullOwner = owner;
    }

    return out;
}
